/* hr-asset-guard.js — casual-copy deterrents (owner asset-protection request, 2026-07-24)
 *
 * HONEST SCOPE: deters casual right-click-save / drag-save / long-press-save.
 * Not real protection -- see hr-asset-guard.css header for the full note.
 *
 * Coordination with hr-conversion-observer (hr-pixel.js): every listener below
 * is attached in the bubble phase (no `true` capture flag) and calls
 * preventDefault() ONLY -- never stopPropagation() / stopImmediatePropagation().
 * That means a contextmenu or dragstart attempt on a guarded image still
 * bubbles all the way to document exactly as if this file did not exist, so
 * the observer keeps seeing it as a signal, today or whenever it adds
 * contextmenu/copy capture of its own.
 *
 * KILL SWITCH: flip HR_ASSET_GUARD_ENABLED to false to disable instantly,
 * site-wide, no other change required (mirrors the hr-shield.js pattern).
 */
(function () {
  'use strict';

  var HR_ASSET_GUARD_ENABLED = true;
  if (!HR_ASSET_GUARD_ENABLED) return;

  var SVG_NS = 'http://www.w3.org/2000/svg';
  var IMAGE_SELECTOR = 'img, video, picture, [style*="background-image"], .hr-protect, .hr-protect-bg';
  var TOAST_MESSAGE = 'Human Renaissance imagery is protected.';
  var TOAST_HOLD_MS = 1600;

  // --- Gentle toast, reused across attempts (never stacks multiple) ------
  var toastEl = null;
  var toastTextEl = null;
  var toastHideTimer = null;

  function buildIcon() {
    var svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', 'hr-guard-toast__icon');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('aria-hidden', 'true');

    var shield = document.createElementNS(SVG_NS, 'path');
    shield.setAttribute('d', 'M12 3l7 3v5c0 4.5-3 8.2-7 9.5-4-1.3-7-5-7-9.5V6l7-3z');
    shield.setAttribute('stroke', 'currentColor');
    shield.setAttribute('stroke-width', '1.8');
    shield.setAttribute('stroke-linejoin', 'round');

    var check = document.createElementNS(SVG_NS, 'path');
    check.setAttribute('d', 'M9 12l2 2 4-4');
    check.setAttribute('stroke', 'currentColor');
    check.setAttribute('stroke-width', '1.8');
    check.setAttribute('stroke-linecap', 'round');
    check.setAttribute('stroke-linejoin', 'round');

    svg.appendChild(shield);
    svg.appendChild(check);
    return svg;
  }

  function buildToast() {
    var el = document.createElement('div');
    el.className = 'hr-guard-toast';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');

    var text = document.createElement('span');
    text.className = 'hr-guard-toast__text';

    el.appendChild(buildIcon());
    el.appendChild(text);
    document.body.appendChild(el);
    toastTextEl = text;
    return el;
  }

  function showToast(message) {
    if (!toastEl) toastEl = buildToast();
    toastTextEl.textContent = message;
    // Force a reflow before re-adding the class so a retrigger (right-click a
    // second image while the toast is still up) always restarts the
    // transition cleanly instead of the browser coalescing the two class
    // changes into a no-op.
    toastEl.classList.remove('is-visible');
    void toastEl.offsetHeight;
    toastEl.classList.add('is-visible');
    window.clearTimeout(toastHideTimer);
    toastHideTimer = window.setTimeout(function () {
      toastEl.classList.remove('is-visible');
    }, TOAST_HOLD_MS);
  }

  // --- Guards --------------------------------------------------------------
  document.addEventListener('contextmenu', function (event) {
    var target = event.target && event.target.closest && event.target.closest(IMAGE_SELECTOR);
    if (!target) return; // right-click anywhere else on the page is untouched
    event.preventDefault();
    showToast(TOAST_MESSAGE);
  });

  document.addEventListener('dragstart', function (event) {
    var target = event.target && event.target.closest && event.target.closest(IMAGE_SELECTOR);
    if (!target) return;
    event.preventDefault();
  });

  // Belt-and-suspenders static pass: draggable=false on every image present
  // at load (this snippet renders just before </body>, so the DOM is already
  // parsed). Images inserted later -- quick-add modal, AJAX cart, lazy
  // sections -- are still covered live by the dragstart delegate above and by
  // -webkit-user-drag:none in hr-asset-guard.css.
  var images = document.querySelectorAll('img, video');
  for (var i = 0; i < images.length; i += 1) {
    images[i].draggable = false;
  }

  // --- Site-wide VIDEO hardening (owner expanded scope, 2026-08-05) --------
  // CSS backgrounds cannot show a <video>, so every <video> on the storefront
  // (not just review clips) gets hardened attributes + a transparent overlay
  // catching right-click/long-press instead of the native player. Applied
  // once per element (data-hr-vid-hardened guard), so re-running this on a
  // MutationObserver pass never double-wraps the same video. Deliberately
  // skips native Shopify checkout (this script never loads there) and any
  // video explicitly opted out via .hr-allow-video-controls (none currently
  // exist -- opt-out hook for the future, e.g. an accessibility-required
  // player).
  function hardenVideo(video) {
    if (!video || video.getAttribute('data-hr-vid-hardened') === '1' || video.closest('.hr-allow-video-controls')) return;
    video.setAttribute('data-hr-vid-hardened', '1');
    video.removeAttribute('controls');
    video.setAttribute('controlsList', 'nodownload noremoteplayback nofullscreen');
    video.disablePictureInPicture = true;
    video.setAttribute('oncontextmenu', 'return false');
    video.addEventListener('contextmenu', function (e) { e.preventDefault(); });

    // Only insert a catcher overlay if the video isn't already the passive
    // background layer of a card that has its own play/pause click handler
    // (those already route clicks through the card, not the <video> itself).
    // If the video's parent isn't positioned, make it a positioning context
    // so the overlay covers exactly the video's box.
    var parent = video.parentElement;
    if (!parent || parent.querySelector(':scope > .hr-video-catcher')) return;
    var computedPosition = window.getComputedStyle(parent).position;
    if (computedPosition === 'static') parent.style.position = 'relative';

    var catcher = document.createElement('div');
    catcher.className = 'hr-video-catcher';
    catcher.setAttribute('aria-hidden', 'true');
    catcher.style.position = 'absolute';
    catcher.style.inset = '0';
    catcher.style.background = 'transparent';
    catcher.style.zIndex = '1';
    catcher.style.pointerEvents = 'none'; // let taps/clicks reach the card's own play button by default
    catcher.addEventListener('contextmenu', function (e) { e.preventDefault(); });
    parent.appendChild(catcher);
  }

  var existingVideos = document.querySelectorAll('video');
  for (var v = 0; v < existingVideos.length; v += 1) hardenVideo(existingVideos[v]);

  // Belt-and-suspenders: images/videos inserted later (AJAX cart, quick-add
  // modal, lazy sections, slider re-renders) also get draggable=false as soon
  // as they appear -- the live dragstart delegate above already covers them,
  // this just keeps the attribute consistent for anything that reads it.
  if (window.MutationObserver) {
    var mediaObserver = new MutationObserver(function (mutations) {
      for (var m = 0; m < mutations.length; m += 1) {
        var added = mutations[m].addedNodes;
        for (var n = 0; n < added.length; n += 1) {
          var node = added[n];
          if (!node || node.nodeType !== 1) continue;
          if (node.matches && node.matches('img, video')) node.draggable = false;
          if (node.matches && node.matches('video')) hardenVideo(node);
          if (node.querySelectorAll) {
            var nested = node.querySelectorAll('img, video');
            for (var j = 0; j < nested.length; j += 1) nested[j].draggable = false;
            var nestedVideos = node.querySelectorAll('video');
            for (var k = 0; k < nestedVideos.length; k += 1) hardenVideo(nestedVideos[k]);
          }
        }
      }
    });
    mediaObserver.observe(document.body, { childList: true, subtree: true });
  }

  // --- Review-image background-image loader (owner request, 2026-08-04) ---
  // Review-card / member-carousel photos are rendered as `.hr-protect-bg`
  // divs with the image URL parked in `data-hr-bg` (see
  // sections/hr-social-reviews-mobile.liquid, hr-video-reviews-desktop.liquid)
  // instead of `<img>`, so there is no element to right-click "Save image
  // as" or drag out. This loader is the only piece that turns that URL into
  // a real background-image, and it only does so once the card is within
  // ~1.5 viewports -- same lazy threshold an `<img loading="lazy">` gives --
  // through the shared HRPerf.whenNear observer (one shared IntersectionObserver
  // for the whole page, no per-card observer, no polling). If hr-perf-core.js
  // has not loaded yet for any reason, this falls back to loading immediately
  // so the review section is never left blank.
  function paintProtectedBg(el) {
    if (!el || el.getAttribute('data-hr-bg-loaded') === '1') return;
    var url = el.getAttribute('data-hr-bg');
    if (!url) return;
    el.style.backgroundImage = 'url("' + url + '")';
    el.setAttribute('data-hr-bg-loaded', '1');
  }

  function initProtectedBackgrounds(root) {
    var scope = root || document;
    var nodes = scope.querySelectorAll ? scope.querySelectorAll('.hr-protect-bg[data-hr-bg]') : [];
    for (var i = 0; i < nodes.length; i += 1) {
      (function (el) {
        if (el.getAttribute('data-hr-bg-loaded') === '1') return;
        if (window.HRPerf && typeof window.HRPerf.whenNear === 'function') {
          window.HRPerf.whenNear(el, function () {
            paintProtectedBg(el);
          }, '150% 0px 150% 0px');
        } else {
          paintProtectedBg(el); // no shared observer available: fail open, load now
        }
      })(nodes[i]);
    }
  }

  initProtectedBackgrounds();

  // Cards render dynamically in some carousels (slider re-init, quick swaps);
  // reuse the same media MutationObserver above instead of adding a second
  // one, so this stays a single subtree watcher for the whole page.
  if (window.MutationObserver) {
    var bgObserver = new MutationObserver(function (mutations) {
      for (var m = 0; m < mutations.length; m += 1) {
        var added = mutations[m].addedNodes;
        for (var n = 0; n < added.length; n += 1) {
          var node = added[n];
          if (!node || node.nodeType !== 1) continue;
          if (node.matches && node.matches('.hr-protect-bg[data-hr-bg]')) initProtectedBackgrounds({ querySelectorAll: function () { return [node]; } });
          if (node.querySelectorAll) initProtectedBackgrounds(node);
        }
      }
    });
    bgObserver.observe(document.body, { childList: true, subtree: true });
  }
})();
